// Reconciles the local mirror with the server through the /sync API.
//
//   pull  — fetch records changed since our cursor, write them into IndexedDB,
//           advance the cursor
//   push  — send queued offline edits, apply the server's merged result
//           (Phase 3 fills the write side; the plumbing lives here now)
//   syncNow — push then pull, best-effort; safe to call on every page load
import crm from '../crm/api';
import {
  putRecords, getMeta, setMeta, getOutbox, clearOutboxItems, queueChange,
} from './db';

const CURSOR_KEY = 'syncCursor';

export async function pull() {
  const since = await getMeta(CURSOR_KEY);
  const { data } = await crm.get('/sync/pull', { params: since ? { since } : {} });
  if (!data?.success) throw new Error(data?.error || 'pull failed');

  await putRecords('leads', data.leads);
  await putRecords('tasks', data.tasks);
  await setMeta(CURSOR_KEY, data.serverTime);   // advance only after a clean write
  return { leads: data.leads.length, tasks: data.tasks.length };
}

export async function push() {
  const outbox = await getOutbox();
  if (!outbox.length) return { pushed: 0, conflicts: [] };

  const changes = outbox.map((o) => o.change);
  const { data } = await crm.post('/sync/push', { changes });
  if (!data?.success) throw new Error(data?.error || 'push failed');

  const conflicts = [];
  const done = [];
  data.results.forEach((res, i) => {
    const localId = outbox[i].localId;
    if (res.status === 'conflict') {
      conflicts.push(res);          // surfaced to the UI in Phase 4; leave queued
      return;
    }
    if (res.record) putRecords('leads', [res.record]);  // keep the merged truth
    done.push(localId);
  });
  await clearOutboxItems(done);
  return { pushed: done.length, conflicts };
}

// Best-effort: never throw into a page. Offline simply no-ops and the local
// mirror keeps serving what it already has.
export async function syncNow() {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { skipped: 'offline' };
  }
  try {
    const pushed = await push();
    const pulled = await pull();
    return { ...pulled, ...pushed };
  } catch (e) {
    return { error: e.message };
  }
}

// Re-exported so pages queue edits without reaching into db.js directly.
export { queueChange };
